<template>
  <div class="home">
    <div class="container">
      <!-- Hero Section -->
      <div class="row justify-content-center">
        <div class="col-12 col-md-10 col-lg-8">
          <!-- Pointing poker graphic -->
          <div class="text-center mb-4">
            <img :src="pointingPokerImage" alt="Pointing Poker" class="img-fluid home-graphic" data-id="home-graphic">
          </div>
          
          <!-- App title and main description -->
          <h1 class="display-4 text-center mb-4">Agile Tool Chest</h1>
          <p class="lead text-center mb-5">
            Conduct collaborative estimation poker and confidence voting sessions with your agile team. 
            Create rooms, share links, and vote together in real-time.
          </p>
          
          <!-- Get Started button -->
          <div class="text-center mb-5">
            <router-link to="/rooms" class="btn btn-primary btn-lg px-5 py-3">
              Get Started...
            </router-link>
          </div>
        </div>
      </div>

      <!-- What ATC Does Section -->
      <div class="row justify-content-center mb-5">
        <div class="col-12 col-lg-10">
          <div class="card shadow-sm">
            <div class="card-body p-4">
              <h2 class="h3 mb-4 text-center">Perfect for Distributed Teams</h2>
              <div class="row">
                <div class="col-md-6 mb-4">
                  <div class="feature-item d-flex">
                    <div class="feature-icon me-3">
                      🎯
                    </div>
                    <div class="feature-content">
                      <h4 class="h5 text-primary mb-2">Estimation Poker</h4>
                      <p class="mb-0">
                        Collaborative story point estimation using planning poker methodology. 
                        Team members vote simultaneously, then reveal estimates together to discuss and align.
                      </p>
                    </div>
                  </div>
                </div>
                <div class="col-md-6 mb-4">
                  <div class="feature-item d-flex">
                    <div class="feature-icon me-3">
                      ✅
                    </div>
                    <div class="feature-content">
                      <h4 class="h5 text-primary mb-2">Confidence Voting</h4>
                      <p class="mb-0">
                        Gauge team confidence levels on decisions, estimates, or project direction. 
                        Quick pulse checks to identify areas needing more discussion.
                      </p>
                    </div>
                  </div>
                </div>
                <div class="col-md-6 mb-4">
                  <div class="feature-item d-flex">
                    <div class="feature-icon me-3">
                      💭
                    </div>
                    <div class="feature-content">
                      <h4 class="h5 text-primary mb-2">Team Retrospectives</h4>
                      <p class="mb-0">
                        Reflect and improve together. Organize ideas into customizable columns, 
                        share your thoughts through sticky notes, and vote on priorities.
                      </p>
                    </div>
                  </div>
                </div>
                <div class="col-md-6 mb-3">
                  <div class="feature-item d-flex">
                    <div class="feature-icon me-3">
                      🔗
                    </div>
                    <div class="feature-content">
                      <h4 class="h5 text-primary mb-2">Easy Room Sharing</h4>
                      <p class="mb-0">
                        Create rooms instantly and share links with your team. 
                        No accounts required — just join and start voting.
                      </p>
                    </div>
                  </div>
                </div>
                <div class="col-md-6 mb-3">
                  <div class="feature-item d-flex">
                    <div class="feature-icon me-3">
                      ⚡
                    </div>
                    <div class="feature-content">
                      <h4 class="h5 text-primary mb-2">Real-time Sync</h4>
                      <p class="mb-0">
                        See votes and participation in real-time. 
                        Built for remote and hybrid teams working across time zones.
                      </p>
                    </div>
                  </div>
                </div>
                <div class="col-md-6 mb-3">
                  <div class="feature-item d-flex">
                    <div class="feature-icon me-3">
                      📱
                    </div>
                    <div class="feature-content">
                      <h4 class="h5 text-primary mb-2">Mobile-First Design</h4>
                      <p class="mb-0">
                        Fully responsive interface optimized for mobile devices. 
                        Vote seamlessly from your phone, tablet, or desktop with touch-friendly controls.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Spec-Driven Development Section -->
      <div class="row justify-content-center">
        <div class="col-12 col-lg-10">
          <div class="card bg-light shadow-sm">
            <div class="card-body p-4">
              <div class="row align-items-start">
                <div class="col-md-8">
                  <h2 class="h3 mb-3">Built using Spec-Driven Development</h2>
                  <p class="mb-3">
                    This application showcases the power of <strong>spec-driven development</strong> using Kiro, 
                    an agentic AI IDE from AWS. Unlike traditional "vibe coding" with AI assistants, the cloud
                    infrastructure, the CI/CD environment as well as every feature 
                    was built from formal specifications that guided full code generation with precision and consistency.
                  </p>
                  <div class="mb-3">
                    <h4 class="h6 text-muted mb-2">Key Highlights:</h4>
                    <ul class="list-unstyled">
                      <li class="mb-1">📋 <strong>EARS Requirements:</strong> Structured, testable requirements</li>
                      <li class="mb-1">🏗️ <strong>Architecture-First:</strong> System design before implementation</li>
                      <li class="mb-1">☁️ <strong>Full-Stack AWS:</strong> Infrastructure as code with CDK</li>
                      <li class="mb-1">🔄 <strong>CI/CD Pipeline:</strong> Automated testing and deployment</li>
                    </ul>
                  </div>
                  <div class="text-muted mb-3">
                    <small>
                      Developed in my spare time over two weeks as a case study in agentic AI development. 
                      A detailed technical article about this journey is coming soon.
                    </small>
                  </div>
                  <div class="d-flex flex-column flex-md-row align-items-center align-items-md-start mb-0">
                    <div class="aws-logo-container mb-3 mb-md-0 me-md-3 mt-1">
                      <a href="https://aws.amazon.com/what-is-cloud-computing">
                        <img src="https://d0.awsstatic.com/logos/powered-by-aws.png" 
                             alt="Powered by AWS Cloud Computing" 
                             class="aws-logo">
                      </a>
                    </div>
                    <div class="aws-disclaimer mb-3 mb-md-0 text-center text-md-start align-self-md-start">
                      <small class="text-muted">
                        Amazon Web Services, AWS, and the Powered by AWS logo are trademarks of Amazon.com, Inc. or its affiliates.
                      </small>
                    </div>
                  </div>
                </div>
                <div class="col-md-4 text-center mt-0 mb-0">
                  <!-- re:Invent photo -->
                  <div class="reinvent-photo pt-0 pb-0">
                    <img :src="houseOfKiroImage" alt="At AWS re:Invent 2025 - House of Kiro" class="img-fluid rounded shadow-sm">
                    <p class="small text-muted mt-2 mb-0">AWS re:Invent 2025<br>The seed of inspiration<br></p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import pointingPokerImage from '@/assets/pointing-poker.png'
import houseOfKiroImage from '@/assets/house-of-kiro.jpg'
</script>

<style scoped>
/* Responsive layout */
.home {
  padding-top: 2rem;
  padding-bottom: 3rem;
  padding-left: 1rem;
  padding-right: 1rem;
}

.home-graphic {
  max-width: 100%;
  height: auto;
  max-height: 300px;
  object-fit: contain;
}

/* Feature items styling */
.feature-item {
  height: 100%;
  align-items: flex-start;
}

.feature-item h4 {
  font-weight: 600;
}

.feature-icon {
  font-size: 1.3rem;
  line-height: 1;
  flex-shrink: 0;
  width: 2.5rem;
  text-align: center;
}

.feature-content {
  flex: 1;
}

/* re:Invent photo styling */
.reinvent-photo {
  padding: 1rem;
}

.reinvent-photo img {
  max-width: 220px;
  max-height: 220px;
  object-fit: cover;
}

/* Larger photo on wider screens */
@media (min-width: 992px) {
  .reinvent-photo img {
    max-width: 260px;
    max-height: 260px;
  }
}

@media (min-width: 1200px) {
  .reinvent-photo img {
    max-width: 300px;
    max-height: 300px;
  }
}

/* Card styling */
.card {
  border: none;
  border-radius: 0.75rem;
}

.card.bg-light {
  background-color: #f8f9fa !important;
}

/* Mobile-first responsive design */
@media (min-width: 576px) {
  .home-graphic {
    max-height: 350px;
  }
}

@media (min-width: 768px) {  
  .home-graphic {
    max-height: 400px;
  }
  
  .feature-item {
    padding-right: 1rem;
  }
  
  .feature-icon {
    font-size: 2rem;
    width: 3rem;
  }
}

/* Ensure button has proper touch target size on mobile */
.btn-lg {
  min-height: 44px;
  min-width: 44px;
}

/* Typography improvements */
.display-4 {
  font-weight: 700;
}

.text-primary {
  color: #0d6efd !important;
}

/* List styling */
.list-unstyled li {
  position: relative;
  padding-left: 0;
}

/* AWS logo styling */
.aws-logo-container {
  flex-shrink: 0;
  width: 120px;
}

.aws-logo {
  width: 100%;
  height: auto;
}

.aws-disclaimer {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

/* Shadow utilities */
.shadow-sm {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075) !important;
}
</style>